import type { Bdkv2Value } from '@/generated/hooks/bdk/models';

const PENDING_OAUTH_CONNECTION_KEY = 'managed-oauth-connection-intent';

/** Consent flows expire server-side after 10 minutes; 5 more allow for a slow return. */
const MAX_AGE_MS = 15 * 60 * 1000;

/**
 * The connection write to make once the credential is signed in, for screens
 * that can't reopen their dialog after the redirect.
 */
export type TConnectOnReturn =
  | {
      action: 'create';
      connectionName: string;
      /** The book's own auth method id. */
      authMethodId: string;
      /** Environment labels, e.g. `['draft', 'published']`. */
      labels: string[];
      configMap?: Record<string, Bdkv2Value>;
    }
  | {
      action: 'update';
      connectionId: string;
      connectionName: string;
      authMethodId: string;
      labels: { key?: string; value?: string }[];
      configMap?: Record<string, Bdkv2Value>;
    };

/** The connection edit a sign-in started from. */
export interface IPendingOAuthEdit {
  /** The connections the edit dialog was opened with. */
  connectionIds: string[];
  /** The environment whose credential is being changed. */
  environment: 'draft' | 'published';
  /** Unsaved connection configuration, restored on return. */
  configMaps?: { draft?: unknown; published?: unknown };
}

/**
 * Connect-wizard state saved before the full-page redirect to an OAuth
 * provider, so the wizard can reopen where it left off when the browser comes
 * back with `?data=<credential id>` or `?error=<reason>`.
 */
export interface IPendingOAuthConnection {
  /** The organization the sign-in started in. */
  organizationId: string;
  /** The workspace the sign-in started in; the credential lives there. */
  workspaceId: string;
  integrationName: string;
  integrationVersion: string;
  connectionName: string;
  /** The auth method picked in the wizard. */
  authMethodId: string;
  /** The credential awaiting consent. */
  credentialId: string;
  /** Set when signing in from the edit dialog rather than the connect wizard. */
  edit?: IPendingOAuthEdit;
  /**
   * Set when signing an existing credential in again (Reconnect). A failed
   * sign-in leaves that credential as it was, so it's never removed.
   */
  reconnect?: boolean;
  /** Set when the connection is written on return instead of in a dialog. */
  connectOnReturn?: TConnectOnReturn;
  /**
   * The screen that started the sign-in, when several on one page can reopen
   * a connect wizard (e.g. a chat card and the automation menu).
   */
  origin?: string;
  savedAt: number;
  /** Set once a wizard has picked up the return, so no other host does. */
  claimed?: boolean;
  /** Set when the user came back without signing in; the credential is unused. */
  abandoned?: boolean;
}

/**
 * Saves the pending connection right before redirecting to the provider.
 *
 * @param pending - Wizard state to restore on return.
 */
export const writePendingOAuthConnection = (
  pending: Omit<IPendingOAuthConnection, 'savedAt'>,
): void => {
  sessionStorage.setItem(
    PENDING_OAUTH_CONNECTION_KEY,
    JSON.stringify({ ...pending, savedAt: Date.now() }),
  );
};

/**
 * Marks the pending connection as picked up. Returns false when another host
 * already claimed it.
 *
 * @returns Whether this caller claimed it.
 */
export const claimPendingOAuthConnection = (): boolean => {
  const pending = readPendingOAuthConnection();
  if (!pending || pending.claimed) return false;
  sessionStorage.setItem(
    PENDING_OAUTH_CONNECTION_KEY,
    JSON.stringify({ ...pending, claimed: true }),
  );
  return true;
};

/** Clears the pending connection. */
export const clearPendingOAuthConnection = (): void => {
  sessionStorage.removeItem(PENDING_OAUTH_CONNECTION_KEY);
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

// The entry is read back from storage, so the write it carries is checked
// before it reaches BDK.
const isConnectOnReturn = (value: unknown): value is TConnectOnReturn => {
  if (
    !isRecord(value) ||
    typeof value.connectionName !== 'string' ||
    typeof value.authMethodId !== 'string' ||
    !Array.isArray(value.labels) ||
    (value.configMap !== undefined &&
      !(
        isRecord(value.configMap) &&
        Object.values(value.configMap).every(isRecord)
      ))
  ) {
    return false;
  }
  if (value.action === 'create') {
    return value.labels.every(label => typeof label === 'string');
  }
  return (
    value.action === 'update' &&
    typeof value.connectionId === 'string' &&
    value.labels.every(isRecord)
  );
};

/**
 * Reads the pending connection, dropping it when it is malformed or stale.
 *
 * @returns The pending connection, or null.
 */
export const readPendingOAuthConnection =
  (): IPendingOAuthConnection | null => {
    try {
      const raw = sessionStorage.getItem(PENDING_OAUTH_CONNECTION_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as Partial<IPendingOAuthConnection>;
      if (
        typeof parsed.organizationId !== 'string' ||
        typeof parsed.workspaceId !== 'string' ||
        typeof parsed.integrationName !== 'string' ||
        typeof parsed.credentialId !== 'string' ||
        typeof parsed.savedAt !== 'number' ||
        Date.now() - parsed.savedAt > MAX_AGE_MS ||
        (parsed.connectOnReturn !== undefined &&
          !isConnectOnReturn(parsed.connectOnReturn))
      ) {
        clearPendingOAuthConnection();
        return null;
      }
      return parsed as IPendingOAuthConnection;
    } catch {
      return null;
    }
  };

/**
 * Query param that marks the page before a managed sign-in starts. Gringotts
 * sends the browser back to that page with `data`/`error` added, so the marker
 * tells a managed return apart from BDK's own OAuth return on the same params.
 */
export const MANAGED_OAUTH_RETURN_PARAM = 'gringotts_oauth';

const replaceMarker = (present: boolean) => {
  const url = new URL(window.location.href);
  if (url.searchParams.has(MANAGED_OAUTH_RETURN_PARAM) === present) return;
  if (present) url.searchParams.set(MANAGED_OAUTH_RETURN_PARAM, '1');
  else url.searchParams.delete(MANAGED_OAUTH_RETURN_PARAM);
  window.history.replaceState(window.history.state, '', url);
};

/**
 * Marks the page as the start of a managed sign-in. Call right before the
 * request that captures the page as the return address.
 */
export const markManagedOAuthReturn = (): void => replaceMarker(true);

/** Removes the marker when the browser didn't leave for the provider after all. */
export const unmarkManagedOAuthReturn = (): void => replaceMarker(false);

const dropAbandonedMarker = () => {
  const params = new URLSearchParams(window.location.search);
  if (
    !params.has(MANAGED_OAUTH_RETURN_PARAM) ||
    params.has('data') ||
    params.has('error')
  ) {
    return;
  }
  unmarkManagedOAuthReturn();
  const pending = readPendingOAuthConnection();
  // A claimed sign-in came back from the provider; its credential may be in use.
  if (pending && !pending.reconnect && !pending.claimed) {
    sessionStorage.setItem(
      PENDING_OAUTH_CONNECTION_KEY,
      JSON.stringify({ ...pending, abandoned: true }),
    );
  }
};

/**
 * Removes the marker left by a sign-in the user backed out of, so a BDK sign-in
 * started from that page isn't taken for a managed return, and flags the new
 * credential for {@link useDiscardAbandonedOAuthSignIn}. Call before the router
 * reads the URL; a page restored by Back is handled too.
 */
export const dropAbandonedManagedOAuthMarker = (): void => {
  dropAbandonedMarker();
  // eslint-disable-next-line no-restricted-syntax -- reason: a page restored from the back/forward cache runs no app code that could react to it
  window.addEventListener('pageshow', event => {
    if (event.persisted) dropAbandonedMarker();
  });
};

/**
 * The URL without the marker, for return addresses handed to BDK.
 *
 * @param href - A URL.
 * @returns The URL, minus the marker when it had one.
 */
export const withoutManagedOAuthMarker = (href: string): string => {
  const url = new URL(href);
  if (!url.searchParams.has(MANAGED_OAUTH_RETURN_PARAM)) return href;
  url.searchParams.delete(MANAGED_OAUTH_RETURN_PARAM);
  return url.toString();
};

/**
 * Whether the URL is the browser coming back from a managed sign-in. BDK's
 * own OAuth handlers must leave these alone: `data` is a credential's id.
 *
 * @param params - The page's query params.
 * @returns True on a managed return.
 */
export const hasManagedOAuthReturn = (params: URLSearchParams): boolean =>
  params.has(MANAGED_OAUTH_RETURN_PARAM) &&
  (params.has('data') || params.has('error'));

/**
 * Whether the pending managed sign-in should handle this return: the URL is
 * a managed return and, on success, it brings back that sign-in's credential.
 *
 * @param params - The page's query params.
 * @returns True when the pending sign-in should handle this return.
 */
export const isManagedOAuthReturn = (params: URLSearchParams): boolean => {
  if (!hasManagedOAuthReturn(params)) return false;
  const pending = readPendingOAuthConnection();
  if (!pending) return false;
  const data = params.get('data');
  return !data || data === pending.credentialId;
};

/**
 * Whether the browser just came back from a connect-wizard sign-in that a
 * wizard should reopen (rather than an edit, Reconnect, or connect-on-return).
 * Lets hosts that only mount on demand (e.g. inside a popover) mount in time.
 *
 * @param origin - Only count sign-ins started from this screen.
 * @returns True on such a return.
 */
export const isReturningToConnectWizard = (origin?: string): boolean => {
  const pending = readPendingOAuthConnection();
  if (
    !pending ||
    pending.edit ||
    pending.reconnect ||
    pending.connectOnReturn
  ) {
    return false;
  }
  if (origin !== undefined && pending.origin !== origin) return false;
  return isManagedOAuthReturn(new URLSearchParams(window.location.search));
};
